/**
 * Transcript Service
 * 
 * Handles API calls for telehealth transcripts and AI documentation.
 */

import api from './api';

export interface TranscriptSegment {
  speaker: string;
  text: string;
  timestamp?: string;
}

export interface Transcript {
  id: string;
  clinic_id: string;
  patient_id: string;
  practitioner_id: string;
  appointment_id?: string;
  encounter_id?: string;
  room_name?: string;
  recording_id?: string;
  transcript_text: string;
  transcript_json?: TranscriptSegment[];
  transcript_format: 'txt' | 'json' | 'vtt' | 'srt';
  ai_summary?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  soap_note_generated: boolean;
  generated_note_id?: string;
  duration_seconds?: number;
  word_count?: number;
  language?: string;
  confidence_score?: number;
  error_message?: string;
  patient_consent_recorded: boolean;
  consent_recorded_at?: string;
  created_at: string;
  processed_at?: string;
  updated_at: string;
  // Joined data
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  practitioner?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  appointment?: {
    id: string;
    start_time: string;
    status: string;
  };
  generatedNote?: {
    id: string;
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
  };
}

export interface GeneratedSOAP {
  id: string;
  note_type: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  is_ai_generated: boolean;
  is_signed: boolean;
  created_at: string;
}

export interface GenerateSOAPResponse {
  success: boolean;
  note: GeneratedSOAP;
  extracted_data?: {
    medications_discussed?: string[];
    labs_ordered?: string[];
    followup_timeframe?: string;
    referrals?: string[];
  };
  icd10_codes?: string[];
  cpt_codes?: string[];
  confidence_score?: number;
}

export interface TranscriptListResponse {
  transcripts: Transcript[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface TranscriptSearchResult extends Transcript {
  highlight?: string;
}

export interface TranscriptSearchResponse {
  transcripts: TranscriptSearchResult[];
  search_term: string;
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface UpdateTranscriptRequest {
  transcript_json?: Array<{
    speaker: string;
    speaker_id?: string;
    text: string;
    timestamp?: string;
    is_final?: boolean;
  }>;
  transcript_text?: string;
}

export interface UpdateTranscriptResponse {
  success: boolean;
  transcript: {
    id: string;
    transcript_text: string;
    transcript_json?: TranscriptSegment[];
    word_count: number;
    updated_at: string;
  };
}

export interface ActionItems {
  prescriptions?: Array<{
    medication: string;
    dose: string;
    frequency: string;
    duration: string;
  }>;
  lab_orders?: Array<{
    test_name: string;
    urgency: 'routine' | 'stat' | 'urgent';
    fasting?: boolean;
  }>;
  imaging_orders?: Array<{
    study: string;
    body_part: string;
    indication: string;
  }>;
  referrals?: Array<{
    specialty: string;
    reason: string;
    urgency: string;
  }>;
  follow_up?: {
    timeframe: string;
    reason: string;
  };
  patient_education?: string[];
  lifestyle_recommendations?: string[];
}

export interface TranscriptLine {
  speakerName: string;
  speakerId?: string;
  text: string;
  timestamp: string;
  isFinal?: boolean;
}

export interface SaveTranscriptRequest {
  patient_id: string;
  appointment_id?: string;
  encounter_id?: string;
  room_name?: string;
  transcript_lines: TranscriptLine[];
  duration_seconds?: number;
  clean_transcript?: boolean;
}

export interface SaveTranscriptResponse {
  success: boolean;
  transcript: {
    id: string;
    patient_id: string;
    appointment_id?: string;
    word_count: number;
    duration_seconds?: number;
    status: string;
    created_at: string;
  };
}

export interface LiveSummaryResponse {
  success: boolean;
  summary: string;
  word_count: number;
}

class TranscriptService {
  /**
   * Save a transcript from a live telehealth call
   */
  async saveTranscript(
    clinicId: string,
    data: SaveTranscriptRequest
  ): Promise<SaveTranscriptResponse> {
    const response = await api.post(`/emr/${clinicId}/transcripts`, data);
    return response.data;
  }

  /**
   * Generate real-time summary from live transcript text
   * Used during active telehealth calls
   */
  async generateLiveSummary(
    clinicId: string,
    transcriptText: string,
    patientName?: string
  ): Promise<LiveSummaryResponse> {
    const response = await api.post(`/emr/${clinicId}/transcripts/live-summary`, {
      transcript_text: transcriptText,
      patient_name: patientName
    });
    return response.data;
  }

  /**
   * Get all transcripts for a clinic
   */
  async getTranscripts(
    clinicId: string,
    options?: {
      patientId?: string;
      status?: 'pending' | 'processing' | 'completed' | 'failed';
      page?: number;
      limit?: number;
    }
  ): Promise<TranscriptListResponse> {
    const params = new URLSearchParams();
    if (options?.patientId) params.append('patientId', options.patientId);
    if (options?.status) params.append('status', options.status);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await api.get(
      `/emr/${clinicId}/transcripts?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Get a single transcript by ID
   */
  async getTranscript(clinicId: string, transcriptId: string): Promise<Transcript> {
    const response = await api.get(`/emr/${clinicId}/transcripts/${transcriptId}`);
    return response.data;
  }

  /**
   * Get transcript for an appointment
   */
  async getAppointmentTranscript(
    clinicId: string,
    appointmentId: string
  ): Promise<Transcript | null> {
    try {
      const response = await api.get(
        `/emr/${clinicId}/appointments/${appointmentId}/transcript`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Generate SOAP note from transcript
   */
  async generateSOAPFromTranscript(
    clinicId: string,
    transcriptId: string
  ): Promise<GenerateSOAPResponse> {
    const response = await api.post(
      `/emr/${clinicId}/transcripts/${transcriptId}/generate-soap`
    );
    return response.data;
  }

  /**
   * Generate SOAP note from encounter data
   */
  async generateSOAPFromEncounter(
    clinicId: string,
    encounterId: string
  ): Promise<GenerateSOAPResponse> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/generate-soap`
    );
    return response.data;
  }

  /**
   * Get clinical summary from transcript
   */
  async getTranscriptSummary(
    clinicId: string,
    transcriptId: string
  ): Promise<{ success: boolean; summary: string; source: 'cached' | 'generated' }> {
    const response = await api.post(
      `/emr/${clinicId}/transcripts/${transcriptId}/summary`
    );
    return response.data;
  }

  /**
   * Extract action items from transcript
   */
  async extractActionItems(
    clinicId: string,
    transcriptId: string
  ): Promise<{ success: boolean; actionItems: ActionItems }> {
    const response = await api.post(
      `/emr/${clinicId}/transcripts/${transcriptId}/action-items`
    );
    return response.data;
  }

  /**
   * Retry failed transcript processing
   */
  async retryTranscript(
    clinicId: string,
    transcriptId: string
  ): Promise<{ success: boolean; message: string; jobId: string }> {
    const response = await api.post(
      `/emr/${clinicId}/transcripts/${transcriptId}/retry`
    );
    return response.data;
  }

  /**
   * Search transcripts by keyword
   */
  async searchTranscripts(
    clinicId: string,
    searchQuery: string,
    options?: {
      patientId?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<TranscriptSearchResponse> {
    const params = new URLSearchParams();
    params.append('q', searchQuery);
    if (options?.patientId) params.append('patientId', options.patientId);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await api.get(
      `/emr/${clinicId}/transcripts/search?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Update a transcript (speaker corrections, text edits)
   */
  async updateTranscript(
    clinicId: string,
    transcriptId: string,
    data: UpdateTranscriptRequest
  ): Promise<UpdateTranscriptResponse> {
    const response = await api.patch(
      `/emr/${clinicId}/transcripts/${transcriptId}`,
      data
    );
    return response.data;
  }
}

export const transcriptService = new TranscriptService();
export default transcriptService;
